<script lang="ts" setup>

import {Source} from "@/domain/type/source.type.ts";
import {onBeforeMount, onBeforeUpdate, ref} from "vue";
import router from "@/router/router.ts";
import {RouteEnum} from "@/router/RouteEnum.ts";
import {useSynchroStore} from "@/stores/synchro.store.ts";
import SourceSelect from "@/components/Source/SourceSelect.vue";
import SourceDetails from "@/components/Source/SourceDetails.vue";
import FusionPart from "@/components/FusionPart.vue";
import HistoryBtn from "@/components/History/HistoryBtn.vue";
import {SynchroType} from "@/domain/type/synchro.type.ts";
import {useWorkflowStore} from "@/stores/workflow.store.ts";

const props = defineProps<{
  sourceLeft: Source,
  sourceRight: Source,
  id: string,
  ownerId?: string,
  type: SynchroType
}>()
const synchroStore = useSynchroStore()
const workflowStore = useWorkflowStore()
const sourceIsChanging = ref(false)
const leftPanelOpen = ref(false)
const rightPanelOpen = ref(false)

function toggleLeftPanel() {
  leftPanelOpen.value = !leftPanelOpen.value
  if (leftPanelOpen.value) {
    rightPanelOpen.value = false
  }
}

function toggleRightPanel() {
  rightPanelOpen.value = !rightPanelOpen.value
  if (rightPanelOpen.value) {
    leftPanelOpen.value = false
  }
}

onBeforeMount(() => {
  reload()
})
onBeforeUpdate(() => {
  if (synchroStore.id !== props.id) {
    reload()
  }
})

function reload() {
  synchroStore.reset()
  synchroStore.sourceLeft = props.sourceLeft
  synchroStore.sourceRight = props.sourceRight
  synchroStore.id = props.id
  synchroStore.type = props.type
  useWorkflowStore().type = props.type
  useWorkflowStore().companyId = props.ownerId ?? props.id

  // Store courseId when type is COURSE or SESSIONS
  if (props.type === SynchroType.COURSE || props.type === SynchroType.SESSIONS) {
    useWorkflowStore().courseId = props.ownerId ?? props.id
  }

  onSourceChange()
}


function onSourceChange() {
  const currentRoute = router.currentRoute.value.name
  sourceIsChanging.value = true
  synchroStore.fetch().then(() => {
    // Extract numGroup for COURSE type
    if ((props.type === SynchroType.COURSE || props.type === SynchroType.SESSIONS) && synchroStore.details) {
      const numGroupField = synchroStore.details.src1.find(detail =>
        detail.label === 'N° groupe' || detail.label === 'Référence'
      )
      if (numGroupField) {
        useWorkflowStore().courseNumGroup = String(numGroupField.val)
      }
    }

    if (currentRoute === RouteEnum.Fusion) {
      router.replace({
        name: currentRoute,
        params: {
          'sourceLeft': synchroStore.sourceLeft,
          'sourceRight': synchroStore.sourceRight,
          'id': synchroStore.id
        }
      })
    }
  }).finally(() => {
    sourceIsChanging.value = false;
  })
}

function onSubmit() {
  synchroStore.submit()
}

</script>

<template>
  <div class="d-flex flex-column h-100" style="position: relative; background: white">
    <v-overlay
      v-if="synchroStore.details" :model-value="sourceIsChanging || synchroStore.isFetching"
      contained
    />

    <!-- Source Selectors -->
    <div class="d-flex w-100 source-selectors">
      <div class="flex-grow-0 source-select-left">
        <source-select
          id="src1Select"
          v-model="synchroStore.sourceLeft"
          :exclude="[synchroStore.sourceRight]"
          @update:modelValue="onSourceChange()"
         :sourceList="workflowStore.config?.sourceLeftList()"
        />
      </div>
      <div class="flex-grow-1 d-flex bg-green align-center fusion-title">
        <span class="text-center align-center w-100">Fusion</span>
      </div>
      <div class="flex-grow-0 source-select-right">
        <source-select
          id="src2Select"
          v-model="synchroStore.sourceRight"
          :exclude="[synchroStore.sourceLeft,Source.SIREN]"
          @update:modelValue="onSourceChange()"
          :sourceList="workflowStore.config?.sourceRightList()"
        />
      </div>
    </div>

    <div class="h-100">
      <div v-if="null !== synchroStore.error && false === synchroStore.isFetching" id="requestMessage" class="d-flex align-center justify-center h-100">
        {{ synchroStore.error}}
      </div>
      <div v-else-if="null === synchroStore.details && false === synchroStore.isFetching" id="requestMessage" class="d-flex align-center justify-center h-100">
        Aucune donnée disponible
      </div>
      <div v-else-if="null !== synchroStore.entity" class="d-flex w-100 fusion-content">
        <!-- Left Panel Details -->
        <div id="src1Lines" class="detail-panel detail-panel-left" :class="{'panel-open': leftPanelOpen}">
          <source-details :source="synchroStore.sourceLeft" :details="synchroStore.details?.src1 ?? []" :link="synchroStore.getSrc1Link()"/>
          <div class="d-flex align-center justify-center pt-5">
            <history-btn v-if="!synchroStore.isFetching" :history="synchroStore.src1History" :source="sourceLeft"/>
          </div>
        </div>

        <!-- Fusion Part -->
        <div id="fusionPart" class="fusion-part-container">
          <fusion-part
            v-model="synchroStore.form"
            :entity="synchroStore.entity"
            :is-changing="sourceIsChanging"
            :request-fields="synchroStore.requestList"
            :request-list-order="synchroStore.requestListOrder"
            @submit="onSubmit()"/>
        </div>

        <!-- Right Panel Details -->
        <div id="src2Lines" class="detail-panel detail-panel-right" :class="{'panel-open': rightPanelOpen}">
          <source-details :link="synchroStore.getSrc2Link()" :source="synchroStore.sourceRight" :details="synchroStore.details?.src2 ?? []"/>
          <div class="d-flex align-center justify-center pt-5">
            <history-btn v-if="!synchroStore.isFetching" :history="synchroStore.src2History" :source="synchroStore.sourceRight"/>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile: Floating buttons to open side panels -->
    <v-btn
      v-if="null !== synchroStore.entity"
      class="floating-btn floating-btn-left"
      icon
      :color="leftPanelOpen ? 'error' : 'primary'"
      size="small"
      elevation="4"
      @click="toggleLeftPanel"
    >
      <v-icon>{{ leftPanelOpen ? 'mdi-close' : 'mdi-information-outline' }}</v-icon>
    </v-btn>

    <v-btn
      v-if="null !== synchroStore.entity"
      class="floating-btn floating-btn-right"
      icon
      :color="rightPanelOpen ? 'error' : 'primary'"
      size="small"
      elevation="4"
      @click="toggleRightPanel"
    >
      <v-icon>{{ rightPanelOpen ? 'mdi-close' : 'mdi-information-outline' }}</v-icon>
    </v-btn>

    <!-- Backdrop overlay for mobile panels -->
    <div
      v-if="(leftPanelOpen || rightPanelOpen)"
      class="panel-backdrop"
      @click="leftPanelOpen = false; rightPanelOpen = false"
    />
  </div>
</template>
<style scoped>
.part {
  height: 100%;
}

/* Desktop layout */
.source-select-left {
  width: 30%;
}

.fusion-title {
  width: 40%;
}

.source-select-right {
  width: 30%;
}

.detail-panel {
  width: 30%;
  background: white;
  overflow-y: auto;
}

.fusion-part-container {
  width: 40%;
}

/* Floating buttons - hidden on desktop */
.floating-btn {
  display: none;
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10000;
}

.floating-btn-left {
  left: 8px;
}

.floating-btn-right {
  right: 8px;
}

/* Backdrop - hidden on desktop */
.panel-backdrop {
  display: none;
}

/* Tablet responsive */
@media (max-width: 960px) {
  .source-select-left,
  .source-select-right {
    width: 28%;
  }

  .fusion-title {
    width: 44%;
  }

  .detail-panel {
    width: 28%;
  }

  .fusion-part-container {
    width: 44%;
  }
}

/* Mobile responsive */
@media (max-width: 600px) {
  /* Keep source selectors on same row */
  .source-selectors {
    flex-direction: row;
    gap: 0;
    padding: 0;
    width: 100%;
  }

  .source-select-left,
  .source-select-right {
    width: 50% !important;
    flex: 1 1 50%;
    min-width: 0;
  }

  .source-select-left :deep(.v-select),
  .source-select-right :deep(.v-select) {
    text-align: center;
  }

  .source-select-left :deep(.v-field__input),
  .source-select-right :deep(.v-field__input) {
    text-align: center;
  }

  /* Hide fusion title on mobile */
  .fusion-title {
    display: none !important;
  }

  /* Fusion content layout */
  .fusion-content {
    position: relative;
  }

  /* Fusion part takes full width */
  .fusion-part-container {
    width: 100% !important;
  }

  /* Detail panels - hidden by default, slide in from sides */
  .detail-panel {
    position: fixed;
    top: 0;
    bottom: 0;
    width: 85%;
    max-width: 320px;
    z-index: 9999;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
    overflow-y: auto;
  }

  .detail-panel-left {
    left: 0;
    transform: translateX(-100%);
  }

  .detail-panel-left.panel-open {
    transform: translateX(0);
  }

  .detail-panel-right {
    right: 0;
    transform: translateX(100%);
  }

  .detail-panel-right.panel-open {
    transform: translateX(0);
  }

  /* Show floating buttons on mobile */
  .floating-btn {
    display: flex;
  }

  /* Show backdrop when panel is open */
  .panel-backdrop {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9998;
    animation: fadeIn 0.3s ease-in-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}
</style>
